import { useEffect, useMemo, useState } from 'react'
import { buildPartnerWhatsAppMessage, buildPartnerWhatsAppUrl } from '../domain/partnerWhatsApp'
import '../partner-whatsapp.css'

interface PartnerWhatsAppDraftProps {
  relationshipStatus: string
  childrenCount: number
}

export function PartnerWhatsAppDraft({ relationshipStatus, childrenCount }: PartnerWhatsAppDraftProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [error, setError] = useState('')
  const context = useMemo(() => ({ relationshipStatus, childrenCount }), [childrenCount, relationshipStatus])
  const message = useMemo(() => buildPartnerWhatsAppMessage(context), [context])

  useEffect(() => {
    if (!dialogOpen) return undefined
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDialogOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [dialogOpen])

  function openDialog() {
    setError('')
    setDialogOpen(true)
  }

  function closeDialog() {
    setDialogOpen(false)
    setError('')
  }

  function openWhatsApp(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const url = buildPartnerWhatsAppUrl(phoneNumber, context)
    if (!url) {
      setError('Enter the full international number with country code, using digits only after the +. Example: +61 412 345 678.')
      return
    }
    window.open(url, '_blank', 'noopener,noreferrer')
    closeDialog()
  }

  return (
    <>
      <section className="partner-whatsapp-feature" aria-labelledby="partner-whatsapp-title">
        <div>
          <p className="eyebrow">Invite her voice—do not assume it</p>
          <h3 id="partner-whatsapp-title">Ask her directly, with care.</h3>
          <p>Create a thoughtful WhatsApp message that explains the work you are doing and invites her to answer this chapter’s questions in her own words.</p>
          <small>Her number stays in this browser and is never saved by PMM. Nothing is sent automatically—you review the draft and choose whether to send it in WhatsApp.</small>
        </div>
        <button type="button" onClick={openDialog}>Create partner WhatsApp draft</button>
      </section>

      {dialogOpen && (
        <div className="partner-whatsapp-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeDialog() }}>
          <section className="partner-whatsapp-dialog" role="dialog" aria-modal="true" aria-labelledby="partner-whatsapp-dialog-title" aria-describedby="partner-whatsapp-dialog-description">
            <header>
              <div>
                <p className="eyebrow">Private, device-only preparation</p>
                <h3 id="partner-whatsapp-dialog-title">Open the message in WhatsApp</h3>
              </div>
              <button type="button" className="partner-whatsapp-close" onClick={closeDialog} aria-label="Close WhatsApp draft dialog">×</button>
            </header>
            <p id="partner-whatsapp-dialog-description">Enter her WhatsApp number in full international format. PMM uses it only to build the <code>wa.me</code> link in this browser.</p>
            <form onSubmit={openWhatsApp}>
              <label>
                <span>Her WhatsApp number</span>
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  autoFocus
                  value={phoneNumber}
                  placeholder="+61 412 345 678"
                  onChange={(event) => { setPhoneNumber(event.target.value); setError('') }}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? 'partner-whatsapp-error partner-whatsapp-number-help' : 'partner-whatsapp-number-help'}
                />
              </label>
              <small id="partner-whatsapp-number-help">Include the country code. Do not include the local leading zero after it.</small>
              {error && <p id="partner-whatsapp-error" className="partner-whatsapp-error" role="alert">{error}</p>}
              <details>
                <summary>Preview the complete message</summary>
                <pre>{message}</pre>
              </details>
              <footer>
                <button type="button" className="partner-whatsapp-cancel" onClick={closeDialog}>Cancel</button>
                <button type="submit" className="partner-whatsapp-open">Open drafted message in WhatsApp</button>
              </footer>
            </form>
          </section>
        </div>
      )}
    </>
  )
}
